import {
  AccountFlagEnum,
  AccountPlanEnum,
  useAccountBillingInfoQuery,
} from '~/generated/graphql';

import { useCreditUsage } from './useCreditUsage';
import useCurrentAccount from './useCurrentAccount';

export const hubspotEnrichmentPlans = [
  AccountPlanEnum.HubspotEnrichmentFree,
  AccountPlanEnum.HubspotEnrichmentPaid,
];

export const hubspotPlans = [
  AccountPlanEnum.HubspotFreeTier,
  AccountPlanEnum.HubspotPaidTier,
];

export const selfServePlans = [
  ...hubspotPlans,
  AccountPlanEnum.FreeTier,
  AccountPlanEnum.PaidTier,
];

export const contractPlans = [AccountPlanEnum.BusinessTier];

export const useEntitlements = () => {
  const { account, currentUser, loading: loadingAccount } = useCurrentAccount();
  const { data: billingInfoData } = useAccountBillingInfoQuery();
  const { hasUsage, loading: loadingUsage } = useCreditUsage();

  const hasFlag = (flag: AccountFlagEnum) => !!account?.flags?.includes(flag);
  const hasPlans = (plans: AccountPlanEnum[]) =>
    !!account?.plans?.some((plan) => plans.includes(plan)) && !loadingAccount;

  const isLoading = loadingAccount || loadingUsage;
  const isOwner = currentUser?.role === 'owner';

  const isContractAccount = hasPlans(contractPlans);
  const isHubspotEnrichment = hasPlans(hubspotEnrichmentPlans);
  const isHubspotAccount = hasPlans(hubspotPlans) || isHubspotEnrichment;
  const isSalesIntelligence = hasPlans(hubspotPlans);
  const isLegacyAccount = !isHubspotAccount && !loadingAccount;
  const isSelfServeAccount = hasPlans(selfServePlans);

  /**
   * Add new entitlements in alphabetical order to keep the list in order
   * and make it easier to find the entitlements.
   *
   * Use entitlements in AppRoutes.tsx and NavBar.tsx to show/hide routes and
   * nav items respectively. Can also use entitlements in components to show/hide
   * content.
   *
   * @example canAccessAdvertising
   */
  const hasAccessAdvertising =
    isLegacyAccount && hasFlag(AccountFlagEnum.AllowPlatformAdvertisingMenu);
  const hasAccessBatch = !isHubspotEnrichment;
  const hasAccessBilling = isOwner && !!billingInfoData?.accountBillingInfo;

  const hasAccessCompanies = isLegacyAccount;
  const hasAccessEnrichment = isLegacyAccount;
  const hasAccessEnrichmentHubspot = isHubspotEnrichment;
  const hasAccessForms = isLegacyAccount;
  const hasAccessFormsHubspot = !isLegacyAccount;
  const hasAccessIntegrations = isLegacyAccount;
  const hasAccessLegacyRevealPlan =
    isLegacyAccount && hasPlans([AccountPlanEnum.Ipcompany]);
  const hasAccessLookup =
    isHubspotEnrichment || isLegacyAccount || isSalesIntelligence;

  const hasAccessPeople = isLegacyAccount;

  const hasAccessPlans = hasPlans(selfServePlans);

  const hasAccessProspector =
    (isSelfServeAccount || isContractAccount) && !isHubspotEnrichment;
  const hasAccessProspectorCRMExports = isHubspotAccount || isContractAccount;
  const hasAccessProspectorExports = hasUsage;

  const hasAccessSettingsApi = isLegacyAccount;
  const hasAccessSettingsApiUsage =
    hasAccessSettingsApi && hasFlag(AccountFlagEnum.AllowApiUsageTab);
  const hasAccessSettingsIntegrations = !isLegacyAccount;
  const hasAccessSettingsSecurity = isLegacyAccount;
  const hasAccessSettingsTeamCreation = isLegacyAccount;
  const hasAccessSettingsTeams = isLegacyAccount;
  const hasAccessSettingsTeamUsers = isLegacyAccount;

  const hasAccessUsage = hasUsage;

  return {
    hasAccessAdvertising,
    hasAccessBatch,
    hasAccessBilling,
    hasAccessCompanies,
    hasAccessSettingsIntegrations,
    hasAccessEnrichment,
    hasAccessEnrichmentHubspot,
    hasAccessForms,
    hasAccessFormsHubspot,
    hasAccessIntegrations,
    hasAccessLegacyRevealPlan,
    hasAccessLookup,
    hasAccessPlans,
    hasAccessPeople,
    hasAccessProspector,
    hasAccessProspectorCRMExports,
    hasAccessProspectorExports,
    hasAccessSettingsApi,
    hasAccessSettingsApiUsage,
    hasAccessSettingsSecurity,
    hasAccessSettingsTeamCreation,
    hasAccessSettingsTeams,
    hasAccessSettingsTeamUsers,
    hasAccessUsage,
    isOwner,
    loading: isLoading,
  };
};
